/**
 * AdminPanel — the gated network-ops console for tech@vfxpop.com.
 *
 * It is the UI face of the SINGLE-CALLABLE admin surface: every control here
 * dispatches an `admin.*` action through {@link GameServices.callAdmin}, which
 * targets the one deployed `api` Cloud Function (owner-mandated: ONE callable,
 * dispatched by an `action` field — see functions/src/contract.ts `ApiAction`).
 * There are no per-action client endpoints; the server re-verifies the
 * `admin === true` custom claim on EVERY call regardless of what this UI shows
 * (docs/BARRAGE_IMPLEMENTATION_PLAN.md "Admin").
 *
 * GATING is defence-in-depth, never a security boundary: the parent should only
 * mount this when {@link GameServices.isAdmin} is true, and we re-check it here
 * and render a polite lock screen otherwise. The real gate is the server claim.
 *
 * ARCHITECTURE (matches the sibling screens): purely presentational +
 * thin orchestration — it holds only screen-local UI state, talks to the backend
 * ONLY through the {@link GameServices} facade, and never touches the game loop
 * or holds per-frame state. Shared shapes come from `services/dataModel` and
 * `core/economy`; the `admin.*` request payloads are mirrored locally as typed
 * helpers (the canonical contract is framework-free in functions/src/contract.ts,
 * outside this app's tsconfig, so we keep the wire shape honest by hand).
 *
 * Two sections, both dense and functional:
 *  - MONITOR    — loads `admin.getMetrics` and shows the six headline numbers.
 *  - ADMINISTRATE — the operator controls: anti-cheat + maintenance toggles,
 *    sticker upsert/delete, post news, manage events, feature a replay, grant
 *    tokens/stickers, ban a user, set a user's tier, and seed starter content.
 *
 * Every mutating control surfaces a transient toast on success and an inline
 * error on failure; nothing is destructive without a typed payload.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Rarity } from '../core/economy';
import { RARITIES } from '../core/economy';
import type { GameServices } from '../services/types';
import type {
  AdminMetrics,
  NewsItem,
  ProfileTier,
  RuntimeConfig,
  StickerCatalogItem,
} from '../services/dataModel';

interface AdminPanelProps {
  /** The tier-aware data facade. All `admin.*` calls are ADMIN-ONLY (server-verified). */
  services: GameServices;
  /** Leave the console (parent owns navigation). */
  onBack: () => void;
}

/** The selectable subscription tiers for the "set tier" control. */
const TIERS: readonly ProfileTier[] = ['free', 'plus', 'pro'];

/** The sticker classes (mirrors {@link StickerCatalogItem.type}). */
const STICKER_TYPES: readonly StickerCatalogItem['type'][] = ['regular', 'seasonal', 'secret'];

/** The prize kinds an event/wheel can award. */
const PRIZE_TYPES = ['tokens', 'xp', 'sticker'] as const;
type PrizeType = (typeof PRIZE_TYPES)[number];

/** Human-readable error text from any thrown value. */
function errorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Something went wrong. Please try again.';
}

export function AdminPanel({ services, onBack }: AdminPanelProps) {
  // Defence-in-depth: bail to a lock screen if the cached claim says non-admin.
  // (The server re-verifies the claim on every call, so this is UX only.)
  if (!services.isAdmin()) {
    return (
      <div className="screen admin-panel">
        <style>{ADMIN_CSS}</style>
        <header className="network-head admin-head">
          <button className="btn btn-ghost network-back" onClick={onBack} type="button">
            ‹ Back
          </button>
          <h1 className="network-title admin-title">Admin</h1>
        </header>
        <section className="panel admin-locked">
          <div className="admin-locked-badge" aria-hidden="true">
            🔒
          </div>
          <h2>Restricted</h2>
          <p className="admin-muted">
            This console is limited to operators holding the <code>admin</code>{' '}
            claim. If you believe you should have access, sign in with the operator
            account.
          </p>
        </section>
      </div>
    );
  }

  return <AdminConsole services={services} onBack={onBack} />;
}

/** The actual console, rendered only once the admin gate has passed. */
function AdminConsole({ services, onBack }: AdminPanelProps) {
  // --- transient feedback ----------------------------------------------------
  const [toast, setToast] = useState<string | null>(null);
  const [globalError, setGlobalError] = useState<string | null>(null);

  /** Flash a transient success toast (auto-clears). */
  const flash = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast((cur) => (cur === msg ? null : cur)), 2600);
  }, []);

  /**
   * Run an `admin.*` action, returning its result (typed by the caller). Centralises
   * busy/error plumbing: clears the global error, surfaces a toast on success, and
   * normalises thrown errors into inline text. The SERVER enforces the admin claim.
   */
  const call = useCallback(
    async <T,>(action: string, payload: unknown, okMsg: string): Promise<T | undefined> => {
      setGlobalError(null);
      try {
        const result = (await services.callAdmin(action, payload)) as T;
        flash(okMsg);
        return result;
      } catch (err) {
        setGlobalError(errorText(err));
        return undefined;
      }
    },
    [services, flash],
  );

  return (
    <div className="screen admin-panel">
      <style>{ADMIN_CSS}</style>

      <header className="network-head admin-head">
        <button className="btn btn-ghost network-back" onClick={onBack} type="button">
          ‹ Back
        </button>
        <h1 className="network-title admin-title">Admin console</h1>
        <span className="admin-mode" data-mode={services.mode}>
          <span className="admin-mode-dot" aria-hidden="true" />
          {services.mode}
        </span>
      </header>

      <p className="admin-tagline">
        Network operations. Every control re-verifies the <code>admin</code> claim
        server-side — this console is the operator face, never the gate.
      </p>

      {globalError && (
        <div className="admin-error-bar" role="alert">
          <span>{globalError}</span>
          <button
            type="button"
            className="admin-error-dismiss"
            onClick={() => setGlobalError(null)}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      <MonitorSection services={services} onError={setGlobalError} />

      <AdministrateSection call={call} />

      {toast && (
        <div className="admin-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// MONITOR — admin.getMetrics
// ===========================================================================

interface MonitorSectionProps {
  services: GameServices;
  onError: (msg: string | null) => void;
}

/** The six headline metrics, in display order. */
const METRIC_META: ReadonlyArray<{
  key: keyof AdminMetrics;
  label: string;
  hint: string;
}> = [
  { key: 'playersOnline', label: 'Players online', hint: 'live presence' },
  { key: 'runsToday', label: 'Runs today', hint: 'current UTC day' },
  { key: 'totalUsers', label: 'Total users', hint: 'named accounts' },
  { key: 'totalRuns', label: 'Total runs', hint: 'all time' },
  { key: 'flaggedRuns', label: 'Flagged runs', hint: 'anti-cheat divergence' },
  { key: 'tokensInCirculation', label: 'Tokens in circulation', hint: 'economy health' },
];

function MonitorSection({ services, onError }: MonitorSectionProps) {
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    onError(null);
    try {
      const m = (await services.callAdmin('admin.getMetrics')) as AdminMetrics;
      setMetrics(m);
    } catch (err) {
      onError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [services, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="panel admin-section">
      <div className="admin-section-head">
        <h2>Monitor</h2>
        <button
          type="button"
          className="btn btn-ghost admin-refresh"
          onClick={() => void load()}
          disabled={loading}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div className="admin-metric-grid">
        {METRIC_META.map((meta) => (
          <div className="admin-metric" key={meta.key}>
            <span className="admin-metric-value">
              {metrics ? (metrics[meta.key] ?? 0).toLocaleString() : '—'}
            </span>
            <span className="admin-metric-label">{meta.label}</span>
            <span className="admin-metric-hint">{meta.hint}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ===========================================================================
// ADMINISTRATE — the operator controls
// ===========================================================================

/** The signature the control cards use to dispatch an admin action. */
type CallFn = <T>(action: string, payload: unknown, okMsg: string) => Promise<T | undefined>;

interface AdministrateSectionProps {
  call: CallFn;
}

function AdministrateSection({ call }: AdministrateSectionProps) {
  return (
    <section className="panel admin-section">
      <div className="admin-section-head">
        <h2>Administrate</h2>
      </div>

      <RuntimeToggles call={call} />

      <div className="admin-cards">
        <SeedStarterCard call={call} />
        <StickerCard call={call} />
        <NewsCard call={call} />
        <EventCard call={call} />
        <FeatureReplayCard call={call} />
        <GrantTokensCard call={call} />
        <GrantStickerCard call={call} />
        <SetTierCard call={call} />
        <BanUserCard call={call} />
      </div>
    </section>
  );
}

// ---- Runtime toggles (anti-cheat + maintenance) ---------------------------

/** The live `config/runtime` toggles. Reads the new config back from the result. */
function RuntimeToggles({ call }: { call: CallFn }) {
  // We don't have a getter for config/runtime here, so the toggles are optimistic
  // until a call returns the authoritative RuntimeConfig, which we then reflect.
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [busy, setBusy] = useState<null | 'antiCheat' | 'maintenance'>(null);

  const setAntiCheat = useCallback(
    async (enabled: boolean) => {
      setBusy('antiCheat');
      const next = await call<RuntimeConfig>(
        'admin.setAntiCheat',
        { enabled },
        `Anti-cheat ${enabled ? 'ENABLED' : 'DISABLED'}.`,
      );
      if (next) setConfig(next);
      setBusy(null);
    },
    [call],
  );

  const setMaintenance = useCallback(
    async (enabled: boolean) => {
      setBusy('maintenance');
      const next = await call<RuntimeConfig>(
        'admin.setMaintenance',
        { enabled },
        `Maintenance ${enabled ? 'ON' : 'OFF'}.`,
      );
      if (next) setConfig(next);
      setBusy(null);
    },
    [call],
  );

  return (
    <div className="admin-toggles">
      <ToggleRow
        label="Anti-cheat (server re-simulation)"
        hint="On: re-run the core sim and reject divergent client scores. Off: trust client score (run still recorded)."
        on={config?.antiCheatEnabled ?? null}
        busy={busy === 'antiCheat'}
        onToggle={(v) => void setAntiCheat(v)}
      />
      <ToggleRow
        label="Maintenance mode"
        hint="On: the app shows a maintenance gate instead of accepting run submissions."
        on={config?.maintenance ?? null}
        busy={busy === 'maintenance'}
        onToggle={(v) => void setMaintenance(v)}
      />
    </div>
  );
}

/** A single explicit ON / OFF toggle row (tristate: unknown until first set). */
function ToggleRow({
  label,
  hint,
  on,
  busy,
  onToggle,
}: {
  label: string;
  hint: string;
  on: boolean | null;
  busy: boolean;
  onToggle: (enabled: boolean) => void;
}) {
  return (
    <div className="admin-toggle-row">
      <div className="admin-toggle-meta">
        <span className="admin-toggle-label">{label}</span>
        <span className="admin-toggle-hint">{hint}</span>
      </div>
      <div className="admin-toggle-buttons" role="group" aria-label={label}>
        <button
          type="button"
          className={'admin-seg' + (on === true ? ' active' : '')}
          disabled={busy}
          aria-pressed={on === true}
          onClick={() => onToggle(true)}
        >
          On
        </button>
        <button
          type="button"
          className={'admin-seg' + (on === false ? ' active' : '')}
          disabled={busy}
          aria-pressed={on === false}
          onClick={() => onToggle(false)}
        >
          Off
        </button>
        <span className="admin-toggle-state">
          {on === null ? 'unknown' : on ? 'enabled' : 'disabled'}
        </span>
      </div>
    </div>
  );
}

// ---- Reusable control-card shell ------------------------------------------

/** A titled control card with a description and a content body (form). */
function ControlCard({
  title,
  desc,
  children,
}: {
  title: string;
  desc: string;
  children: React.ReactNode;
}) {
  return (
    <div className="admin-card">
      <div className="admin-card-head">
        <h3>{title}</h3>
        <p className="admin-card-desc">{desc}</p>
      </div>
      <div className="admin-card-body">{children}</div>
    </div>
  );
}

/** A labelled text/number input field used across the control cards. */
function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'number' | 'url';
  placeholder?: string;
}) {
  return (
    <label className="admin-field">
      <span className="admin-field-label">{label}</span>
      <input
        className="admin-input"
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

/** The card submit button. */
function SubmitButton({
  busy,
  label,
  busyLabel = 'Working…',
  variant = 'primary',
}: {
  busy: boolean;
  label: string;
  busyLabel?: string;
  variant?: 'primary' | 'danger';
}) {
  return (
    <button
      type="submit"
      className={'btn admin-submit ' + (variant === 'danger' ? 'admin-danger' : 'btn-primary')}
      disabled={busy}
    >
      {busy ? busyLabel : label}
    </button>
  );
}

// ---- Seed starter content -------------------------------------------------

function SeedStarterCard({ call }: { call: CallFn }) {
  const [busy, setBusy] = useState(false);
  const run = useCallback(async () => {
    setBusy(true);
    await call('admin.seedStarter', {}, 'Seeded starter content.');
    setBusy(false);
  }, [call]);
  return (
    <ControlCard
      title="Seed starter content"
      desc="Idempotently writes a baseline sticker catalog, the default wheel, and config/runtime defaults so a fresh project is playable."
    >
      <button
        type="button"
        className="btn btn-primary admin-submit"
        disabled={busy}
        onClick={() => void run()}
      >
        {busy ? 'Seeding…' : 'Seed starter content'}
      </button>
    </ControlCard>
  );
}

// ---- Sticker catalog upsert / delete --------------------------------------

function StickerCard({ call }: { call: CallFn }) {
  const [id, setId] = useState('');
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [type, setType] = useState<StickerCatalogItem['type']>('regular');
  const [category, setCategory] = useState('');
  const [series, setSeries] = useState('');
  const [num, setNum] = useState('1');
  const [rarity, setRarity] = useState<Rarity>('common');
  const [imageUrl, setImageUrl] = useState('');
  const [busy, setBusy] = useState<null | 'upsert' | 'delete'>(null);

  const upsert = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!id.trim()) return;
      setBusy('upsert');
      const payload: StickerCatalogItem = {
        id: id.trim(),
        title: title.trim(),
        desc: desc.trim(),
        type,
        category: category.trim(),
        series: series.trim(),
        num: Number(num) || 0,
        rarity,
        imageUrl: imageUrl.trim(),
      };
      await call('admin.upsertSticker', payload, `Saved sticker "${payload.id}".`);
      setBusy(null);
    },
    [call, id, title, desc, type, category, series, num, rarity, imageUrl],
  );

  const remove = useCallback(async () => {
    if (!id.trim()) return;
    setBusy('delete');
    await call('admin.deleteSticker', { id: id.trim() }, `Deleted sticker "${id.trim()}".`);
    setBusy(null);
  }, [call, id]);

  return (
    <ControlCard
      title="Sticker catalog"
      desc="Create or replace a catalogStickers/{id} doc, or delete one by id. The id is the document id."
    >
      <form className="admin-form" onSubmit={upsert}>
        <Field label="Id" value={id} onChange={setId} placeholder="reg-neon-01" />
        <Field label="Title" value={title} onChange={setTitle} placeholder="Neon Pop" />
        <Field label="Description" value={desc} onChange={setDesc} />
        <div className="admin-field-row">
          <label className="admin-field">
            <span className="admin-field-label">Type</span>
            <select
              className="admin-input"
              value={type}
              onChange={(e) => setType(e.target.value as StickerCatalogItem['type'])}
            >
              {STICKER_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="admin-field">
            <span className="admin-field-label">Rarity</span>
            <select
              className="admin-input"
              value={rarity}
              onChange={(e) => setRarity(e.target.value as Rarity)}
            >
              {RARITIES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="admin-field-row">
          <Field label="Category" value={category} onChange={setCategory} />
          <Field label="Series" value={series} onChange={setSeries} />
          <Field label="Number" value={num} onChange={setNum} type="number" />
        </div>
        <Field label="Image URL" value={imageUrl} onChange={setImageUrl} type="url" />
        <div className="admin-actions">
          <SubmitButton busy={busy === 'upsert'} label="Save sticker" busyLabel="Saving…" />
          <button
            type="button"
            className="btn admin-submit admin-danger"
            disabled={busy !== null || !id.trim()}
            onClick={() => void remove()}
          >
            {busy === 'delete' ? 'Deleting…' : 'Delete by id'}
          </button>
        </div>
      </form>
    </ControlCard>
  );
}

// ---- Post news ------------------------------------------------------------

function NewsCard({ call }: { call: CallFn }) {
  const [id, setId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [lang, setLang] = useState('en');
  const [order, setOrder] = useState('0');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!id.trim()) return;
      setBusy(true);
      const payload: NewsItem = {
        id: id.trim(),
        title: title.trim(),
        body: body.trim(),
        imageUrl: imageUrl.trim(),
        lang: lang.trim() || 'en',
        order: Number(order) || 0,
        publishedAt: Date.now(),
      };
      await call('admin.upsertNews', payload, `Posted news "${payload.id}".`);
      setBusy(false);
    },
    [call, id, title, body, imageUrl, lang, order],
  );

  return (
    <ControlCard
      title="Post news"
      desc="Create or replace a news slide (catalogNews/{id}). Sorted ascending by order in the feed."
    >
      <form className="admin-form" onSubmit={submit}>
        <Field label="Id" value={id} onChange={setId} placeholder="launch-2026" />
        <Field label="Title" value={title} onChange={setTitle} />
        <label className="admin-field">
          <span className="admin-field-label">Body</span>
          <textarea
            className="admin-input admin-textarea"
            value={body}
            rows={3}
            onChange={(e) => setBody(e.target.value)}
          />
        </label>
        <Field label="Image URL" value={imageUrl} onChange={setImageUrl} type="url" />
        <div className="admin-field-row">
          <Field label="Language" value={lang} onChange={setLang} placeholder="en" />
          <Field label="Order" value={order} onChange={setOrder} type="number" />
        </div>
        <div className="admin-actions">
          <SubmitButton busy={busy} label="Post news" busyLabel="Posting…" />
        </div>
      </form>
    </ControlCard>
  );
}

// ---- Manage events --------------------------------------------------------

function EventCard({ call }: { call: CallFn }) {
  const [id, setId] = useState('');
  const [active, setActive] = useState(true);
  const [prizeType, setPrizeType] = useState<PrizeType>('tokens');
  const [qty, setQty] = useState('100');
  const [stickerId, setStickerId] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [busy, setBusy] = useState(false);

  /** Parse a datetime-local string to epoch ms (falls back to now / +7d). */
  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!id.trim()) return;
      setBusy(true);
      const start = startsAt ? Date.parse(startsAt) : Date.now();
      const end = endsAt ? Date.parse(endsAt) : start + 7 * 24 * 60 * 60 * 1000;
      const payload = {
        id: id.trim(),
        active,
        prizeType,
        qty: Number(qty) || 0,
        ...(prizeType === 'sticker' ? { stickerId: stickerId.trim() } : {}),
        startsAt: Number.isFinite(start) ? start : Date.now(),
        endsAt: Number.isFinite(end) ? end : Date.now(),
      };
      await call('admin.upsertEvent', payload, `Saved event "${payload.id}".`);
      setBusy(false);
    },
    [call, id, active, prizeType, qty, stickerId, startsAt, endsAt],
  );

  return (
    <ControlCard
      title="Manage events"
      desc="Create or replace a timed event: an active window plus a one-off prize (tokens / xp / sticker)."
    >
      <form className="admin-form" onSubmit={submit}>
        <Field label="Id" value={id} onChange={setId} placeholder="summer-2026" />
        <div className="admin-field-row">
          <label className="admin-field">
            <span className="admin-field-label">Prize type</span>
            <select
              className="admin-input"
              value={prizeType}
              onChange={(e) => setPrizeType(e.target.value as PrizeType)}
            >
              {PRIZE_TYPES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <Field label="Quantity" value={qty} onChange={setQty} type="number" />
          <label className="admin-field admin-field-check">
            <span className="admin-field-label">Active</span>
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
          </label>
        </div>
        {prizeType === 'sticker' && (
          <Field label="Sticker id" value={stickerId} onChange={setStickerId} />
        )}
        <div className="admin-field-row">
          <label className="admin-field">
            <span className="admin-field-label">Starts</span>
            <input
              className="admin-input"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </label>
          <label className="admin-field">
            <span className="admin-field-label">Ends</span>
            <input
              className="admin-input"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
            />
          </label>
        </div>
        <div className="admin-actions">
          <SubmitButton busy={busy} label="Save event" busyLabel="Saving…" />
        </div>
      </form>
    </ControlCard>
  );
}

// ---- Feature a replay -----------------------------------------------------

function FeatureReplayCard({ call }: { call: CallFn }) {
  const [runId, setRunId] = useState('');
  const [busy, setBusy] = useState<null | 'on' | 'off'>(null);

  const setFeatured = useCallback(
    async (featured: boolean) => {
      if (!runId.trim()) return;
      setBusy(featured ? 'on' : 'off');
      await call(
        'admin.featureReplay',
        { runId: runId.trim(), featured },
        featured ? `Featured run "${runId.trim()}".` : `Un-featured run "${runId.trim()}".`,
      );
      setBusy(null);
    },
    [call, runId],
  );

  return (
    <ControlCard
      title="Feature a replay"
      desc="Set or clear the featured flag on a runs/{runId} doc — featured runs surface on the LIVE feed."
    >
      <form className="admin-form" onSubmit={(e) => e.preventDefault()}>
        <Field label="Run id" value={runId} onChange={setRunId} placeholder="run_abc123" />
        <div className="admin-actions">
          <button
            type="button"
            className="btn btn-primary admin-submit"
            disabled={busy !== null || !runId.trim()}
            onClick={() => void setFeatured(true)}
          >
            {busy === 'on' ? 'Featuring…' : 'Feature'}
          </button>
          <button
            type="button"
            className="btn btn-ghost admin-submit"
            disabled={busy !== null || !runId.trim()}
            onClick={() => void setFeatured(false)}
          >
            {busy === 'off' ? 'Clearing…' : 'Un-feature'}
          </button>
        </div>
      </form>
    </ControlCard>
  );
}

// ---- Grant tokens ---------------------------------------------------------

function GrantTokensCard({ call }: { call: CallFn }) {
  const [uid, setUid] = useState('');
  const [amount, setAmount] = useState('100');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!uid.trim()) return;
      setBusy(true);
      const amt = Number(amount) || 0;
      await call(
        'admin.grantTokens',
        { uid: uid.trim(), amount: amt },
        `${amt >= 0 ? 'Granted' : 'Deducted'} ${Math.abs(amt).toLocaleString()} tokens.`,
      );
      setBusy(false);
    },
    [call, uid, amount],
  );

  return (
    <ControlCard
      title="Grant tokens"
      desc="Grant (or deduct, with a negative amount) tokens to a user. Balance never drops below 0 server-side."
    >
      <form className="admin-form" onSubmit={submit}>
        <Field label="User uid" value={uid} onChange={setUid} />
        <Field
          label="Amount (negative deducts)"
          value={amount}
          onChange={setAmount}
          type="number"
        />
        <div className="admin-actions">
          <SubmitButton busy={busy} label="Apply tokens" busyLabel="Applying…" />
        </div>
      </form>
    </ControlCard>
  );
}

// ---- Grant sticker --------------------------------------------------------

function GrantStickerCard({ call }: { call: CallFn }) {
  const [uid, setUid] = useState('');
  const [stickerId, setStickerId] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!uid.trim() || !stickerId.trim()) return;
      setBusy(true);
      await call(
        'admin.grantSticker',
        { uid: uid.trim(), stickerId: stickerId.trim() },
        `Granted "${stickerId.trim()}" to ${uid.trim()}.`,
      );
      setBusy(false);
    },
    [call, uid, stickerId],
  );

  return (
    <ControlCard
      title="Grant sticker"
      desc="Grant a sticker to a user directly (no token cost)."
    >
      <form className="admin-form" onSubmit={submit}>
        <Field label="User uid" value={uid} onChange={setUid} />
        <Field label="Sticker id" value={stickerId} onChange={setStickerId} />
        <div className="admin-actions">
          <SubmitButton busy={busy} label="Grant sticker" busyLabel="Granting…" />
        </div>
      </form>
    </ControlCard>
  );
}

// ---- Set tier -------------------------------------------------------------

function SetTierCard({ call }: { call: CallFn }) {
  const [uid, setUid] = useState('');
  const [tier, setTier] = useState<ProfileTier>('free');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!uid.trim()) return;
      setBusy(true);
      await call(
        'admin.setTier',
        { uid: uid.trim(), tier },
        `Set ${uid.trim()} to "${tier}".`,
      );
      setBusy(false);
    },
    [call, uid, tier],
  );

  return (
    <ControlCard
      title="Set user tier"
      desc="Set a user's subscription tier (drives the replay-star quota: free → 1, plus → 5, pro → 25)."
    >
      <form className="admin-form" onSubmit={submit}>
        <Field label="User uid" value={uid} onChange={setUid} />
        <label className="admin-field">
          <span className="admin-field-label">Tier</span>
          <select
            className="admin-input"
            value={tier}
            onChange={(e) => setTier(e.target.value as ProfileTier)}
          >
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <div className="admin-actions">
          <SubmitButton busy={busy} label="Set tier" busyLabel="Setting…" />
        </div>
      </form>
    </ControlCard>
  );
}

// ---- Ban user -------------------------------------------------------------

function BanUserCard({ call }: { call: CallFn }) {
  const [uid, setUid] = useState('');
  const [busy, setBusy] = useState<null | 'ban' | 'unban'>(null);

  const setBanned = useCallback(
    async (banned: boolean) => {
      if (!uid.trim()) return;
      setBusy(banned ? 'ban' : 'unban');
      await call(
        'admin.banUser',
        { uid: uid.trim(), banned },
        banned ? `Banned ${uid.trim()}.` : `Unbanned ${uid.trim()}.`,
      );
      setBusy(null);
    },
    [call, uid],
  );

  return (
    <ControlCard
      title="Ban user"
      desc="Set or clear a user's banned flag — a ban blocks run submission and economy actions."
    >
      <form className="admin-form" onSubmit={(e) => e.preventDefault()}>
        <Field label="User uid" value={uid} onChange={setUid} />
        <div className="admin-actions">
          <button
            type="button"
            className="btn admin-submit admin-danger"
            disabled={busy !== null || !uid.trim()}
            onClick={() => void setBanned(true)}
          >
            {busy === 'ban' ? 'Banning…' : 'Ban'}
          </button>
          <button
            type="button"
            className="btn btn-ghost admin-submit"
            disabled={busy !== null || !uid.trim()}
            onClick={() => void setBanned(false)}
          >
            {busy === 'unban' ? 'Unbanning…' : 'Unban'}
          </button>
        </div>
      </form>
    </ControlCard>
  );
}

// ===========================================================================
// Screen-scoped styles
//
// Co-located (rather than in src/index.css, owned by another agent) so the
// component is self-contained; everything is nested under `.admin-panel` so it
// can't leak. Shared primitives (`.screen`, `.panel`, `.btn`, CSS vars) come
// from index.css; this only adds the dense console-specific layout.
// ===========================================================================
const ADMIN_CSS = `
/* The console runs wider than the 440px network screens: it's a dense ops grid.
   All colours/blur/radius/shadow come from the glass tokens in index.css; the
   POPSTAR palette re-map (.admin-panel) tints the accents purple/magenta. */
.admin-panel { align-items: stretch; gap: 16px; }

/* ---- Header ---- */
.admin-panel .admin-head {
  width: min(960px, 96vw);
  margin: 0 auto;
  margin-top: 4px;
}
.admin-panel .admin-title { font-size: 26px; letter-spacing: 0.02em; }
.admin-panel .admin-mode {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--muted);
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  padding: 6px 13px;
  backdrop-filter: blur(var(--blur));
  -webkit-backdrop-filter: blur(var(--blur));
}
.admin-panel .admin-mode-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--muted);
  box-shadow: 0 0 6px var(--muted);
}
.admin-panel .admin-mode[data-mode='firebase'] {
  color: var(--success);
  border-color: rgba(46, 204, 113, 0.4);
}
.admin-panel .admin-mode[data-mode='firebase'] .admin-mode-dot {
  background: var(--success);
  box-shadow: 0 0 8px var(--success);
}

.admin-panel .admin-tagline {
  width: min(960px, 96vw);
  margin: -8px auto 0;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.5;
}

/* ---- Error bar ---- */
.admin-panel .admin-error-bar {
  width: min(960px, 96vw);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: linear-gradient(135deg, rgba(239, 71, 111, 0.18), rgba(239, 71, 111, 0.08));
  border: 1px solid rgba(239, 71, 111, 0.5);
  color: #ffd2dd;
  border-radius: var(--radius);
  padding: 12px 16px;
  font-size: 14px;
  box-shadow: var(--shadow-1);
  backdrop-filter: blur(var(--blur));
  -webkit-backdrop-filter: blur(var(--blur));
  animation: fade-in var(--t) var(--ease) both;
}
.admin-panel .admin-error-dismiss {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: var(--radius-sm);
  transition: background var(--t-fast) var(--ease);
}
.admin-panel .admin-error-dismiss:hover { background: rgba(255, 255, 255, 0.1); }

/* ---- Sections (the panel itself is glass via .panel) ---- */
.admin-panel .admin-section {
  width: min(960px, 96vw);
  margin: 0 auto;
  padding: 20px 22px;
}
.admin-panel .admin-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.admin-panel .admin-section-head h2 {
  margin: 0;
  font-size: 13px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
.admin-panel .admin-refresh { padding: 7px 16px; font-size: 13px; }

/* ---- Lock screen ---- */
.admin-panel .admin-locked {
  width: min(440px, 92vw);
  margin: 0 auto;
  text-align: center;
}
.admin-panel .admin-locked-badge {
  font-size: 34px;
  width: 64px;
  height: 64px;
  margin: 0 auto 12px;
  display: grid;
  place-items: center;
  border-radius: var(--radius);
  background: var(--panel-2);
  border: 1px solid var(--glass-border-strong);
}
.admin-panel .admin-locked h2 {
  text-transform: none;
  letter-spacing: 0;
  color: var(--text);
  font-size: 20px;
}
.admin-panel .admin-muted { color: var(--muted); margin: 0; line-height: 1.5; }
.admin-panel code {
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-sm);
  padding: 1px 6px;
  font-size: 0.88em;
  color: var(--accent-soft);
}

/* ---- Monitor metrics ---- */
.admin-panel .admin-metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}
.admin-panel .admin-metric {
  position: relative;
  overflow: hidden;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  box-shadow: var(--shadow-1);
  transition: border-color var(--t) var(--ease), transform var(--t-fast) var(--ease);
}
.admin-panel .admin-metric::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 3px;
  height: 100%;
  background: linear-gradient(180deg, var(--accent), var(--accent-2));
  opacity: 0.7;
}
.admin-panel .admin-metric:hover {
  border-color: var(--glass-border-strong);
  transform: translateY(-2px);
}
.admin-panel .admin-metric-value {
  font-size: 28px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
  color: var(--text);
}
.admin-panel .admin-metric-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-dim);
}
.admin-panel .admin-metric-hint {
  font-size: 11px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ---- Runtime toggles ---- */
.admin-panel .admin-toggles {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}
.admin-panel .admin-toggle-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius);
  padding: 14px 16px;
  box-shadow: var(--shadow-1);
}
.admin-panel .admin-toggle-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex: 1 1 300px;
}
.admin-panel .admin-toggle-label { font-weight: 700; font-size: 14px; color: var(--text); }
.admin-panel .admin-toggle-hint { font-size: 12px; color: var(--muted); line-height: 1.4; }
.admin-panel .admin-toggle-buttons { display: flex; align-items: center; gap: 8px; }

/* Segmented On/Off control — glass pill with an accent-fill active state. */
.admin-panel .admin-seg {
  font: inherit;
  font-weight: 700;
  font-size: 13px;
  border: 1px solid var(--glass-border-strong);
  background: var(--panel-strong);
  color: var(--text);
  border-radius: var(--radius-sm);
  padding: 7px 18px;
  cursor: pointer;
  transition: background var(--t-fast) var(--ease),
    border-color var(--t-fast) var(--ease), color var(--t-fast) var(--ease),
    box-shadow var(--t-fast) var(--ease);
}
.admin-panel .admin-seg:hover:not(:disabled):not(.active) { border-color: var(--accent); }
.admin-panel .admin-seg.active {
  background: linear-gradient(135deg, var(--accent), var(--accent-soft));
  color: var(--accent-ink);
  border-color: transparent;
  box-shadow: 0 4px 14px var(--accent-glow);
}
.admin-panel .admin-seg:disabled { opacity: 0.5; cursor: not-allowed; }
.admin-panel .admin-toggle-state {
  font-size: 11px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  min-width: 60px;
  font-weight: 700;
}

/* ---- Control cards ---- */
.admin-panel .admin-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 14px;
}
.admin-panel .admin-card {
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: var(--shadow-1);
  transition: border-color var(--t) var(--ease), box-shadow var(--t) var(--ease);
}
.admin-panel .admin-card:focus-within {
  border-color: var(--glass-border-strong);
  box-shadow: var(--shadow-2);
}
.admin-panel .admin-card-head h3 {
  margin: 0 0 3px;
  font-size: 15px;
  color: var(--text);
}
.admin-panel .admin-card-desc {
  margin: 0;
  font-size: 12px;
  color: var(--muted);
  line-height: 1.4;
}
.admin-panel .admin-card-body { display: flex; flex-direction: column; }

.admin-panel .admin-form { display: flex; flex-direction: column; gap: 10px; }
.admin-panel .admin-field { display: flex; flex-direction: column; gap: 4px; flex: 1; }
.admin-panel .admin-field-label {
  font-size: 11px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
}
.admin-panel .admin-field-row { display: flex; gap: 10px; flex-wrap: wrap; }
.admin-panel .admin-field-check {
  flex: 0 0 auto;
  justify-content: flex-end;
  align-items: center;
}
.admin-panel .admin-field-check input {
  width: 20px;
  height: 20px;
  accent-color: var(--accent);
  cursor: pointer;
}

/* Inputs sit on the strong glass surface, matching .field-input in index.css. */
.admin-panel .admin-input {
  font: inherit;
  font-size: 14px;
  background: var(--panel-strong);
  color: var(--text);
  border: 1px solid var(--glass-border-strong);
  border-radius: var(--radius-sm);
  padding: 9px 12px;
  width: 100%;
  -webkit-user-select: text;
  user-select: text;
  transition: border-color var(--t-fast) var(--ease), box-shadow var(--t-fast) var(--ease);
}
.admin-panel .admin-input::placeholder { color: var(--muted); opacity: 0.7; }
.admin-panel .admin-input:focus {
  outline: none;
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--accent-tint);
}
.admin-panel select.admin-input { appearance: none; cursor: pointer; }
.admin-panel .admin-textarea { resize: vertical; min-height: 60px; line-height: 1.4; }

.admin-panel .admin-actions { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 4px; }
.admin-panel .admin-submit { padding: 10px 18px; font-size: 14px; }
/* Destructive controls use the shared danger gradient. */
.admin-panel .admin-danger {
  background: linear-gradient(135deg, var(--danger), #c0392b);
  color: #fff;
  box-shadow: 0 8px 24px rgba(239, 71, 111, 0.32);
}
.admin-panel .admin-danger:hover:not(:disabled) { filter: brightness(1.06); }

/* ---- Toast (matches .shop-toast / .wheel-toast) ---- */
.admin-panel .admin-toast {
  position: fixed;
  left: 50%;
  bottom: calc(env(safe-area-inset-bottom) + 24px);
  transform: translateX(-50%);
  z-index: var(--z-toast);
  background: var(--panel-strong);
  color: var(--text);
  border: 1px solid var(--accent);
  border-radius: var(--radius-pill);
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: var(--shadow-3);
  backdrop-filter: blur(var(--blur));
  -webkit-backdrop-filter: blur(var(--blur));
  animation: pop-up var(--t-slow) var(--ease-out) both;
}

@media (max-width: 380px) {
  .admin-panel .admin-section { padding: 16px; }
  .admin-panel .admin-cards { grid-template-columns: 1fr; }
  .admin-panel .admin-toggle-buttons { width: 100%; justify-content: flex-start; }
}
`;
